import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../App';
import { toast } from 'react-toastify';

const ManualPaymentsList = ({ session }) => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('pending'); // pending, approved, rejected
    const [selectedImage, setSelectedImage] = useState(null);

    useEffect(() => {
        fetchRequests();
    }, [filter]);

    const fetchRequests = async () => {
        setLoading(true);
        try {
            const token = session?.access_token || localStorage.getItem('admin_token');

            if (!token) {
                console.warn('No token available for ManualPaymentsList');
                // If we are loading and no token, maybe wait or just return
                setLoading(false);
                return;
            }

            const response = await fetch(`${API_BASE}/payments/manual?status=${filter}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setRequests(data);
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to load payments');
        } finally {
            setLoading(false);
        }
    };

    const handleAction = async (id, action) => {
        if (!window.confirm(`Are you sure you want to ${action} this payment?`)) return;

        try {
            // if rejecting, maybe ask for reason? Using simple confirm for now.
            const reason = action === 'reject' ? prompt("Enter rejection reason:") : null;
            if (action === 'reject' && !reason) return;

            const url = `${API_BASE}/payments/manual/${id}/${action}`;
            const body = action === 'reject' ? { reason } : {};
            const token = session?.access_token || localStorage.getItem('admin_token');

            if (!token) throw new Error('No authentication token found');

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(body)
            });

            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.error || 'Action failed');
            }

            toast.success(`Request ${action}ed successfully`);
            fetchRequests();
        } catch (error) {
            console.error(error);
            toast.error(error.message);
        }
    };

    return (
        <div className="card">
            <h3 style={{ marginBottom: '20px' }}>Manual Payment Requests</h3>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                {['pending', 'approved', 'rejected'].map(status => (
                    <button
                        key={status}
                        className={`btn ${filter === status ? 'btn-primary' : 'btn-outline'}`}
                        onClick={() => setFilter(status)}
                        style={{ textTransform: 'capitalize' }}
                    >
                        {status}
                    </button>
                ))}
            </div>

            {loading ? (
                <p>Loading...</p>
            ) : requests.length === 0 ? (
                <p>No {filter} requests found.</p>
            ) : (
                <div className="table-container">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>User</th>
                                <th>Amount</th>
                                <th>Tx ID / UTR</th>
                                <th>Screenshot</th>
                                {filter === 'pending' && <th>Action</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {requests.map(req => (
                                <tr key={req.id}>
                                    <td>{new Date(req.created_at).toLocaleDateString()}</td>
                                    <td>
                                        <div>{req.profiles?.display_name || 'User'}</div>
                                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>@{req.profiles?.username}</div>
                                    </td>
                                    <td style={{ fontWeight: 'bold' }}>₹{req.amount}</td>
                                    <td>{req.transaction_id}</td>
                                    <td>
                                        {req.screenshot_url ? (
                                            <button
                                                className="btn btn-sm btn-info"
                                                onClick={() => setSelectedImage(req.screenshot_url)}
                                            >
                                                View
                                            </button>
                                        ) : <span className="text-muted">No Image</span>}
                                    </td>
                                    {filter === 'pending' && (
                                        <td>
                                            <div style={{ display: 'flex', gap: '5px' }}>
                                                <button
                                                    className="btn btn-sm btn-success"
                                                    onClick={() => handleAction(req.id, 'approve')}
                                                >
                                                    ✓
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-danger"
                                                    onClick={() => handleAction(req.id, 'reject')}
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Image Modal */}
            {selectedImage && (
                <div className="modal-overlay" onClick={() => setSelectedImage(null)}>
                    <div className="modal" style={{ maxWidth: '80vw', maxHeight: '80vh', padding: 0 }} onClick={e => e.stopPropagation()}>
                        <div style={{ position: 'relative' }}>
                            <button
                                onClick={() => setSelectedImage(null)}
                                style={{
                                    position: 'absolute',
                                    top: '10px',
                                    right: '10px',
                                    background: 'rgba(0,0,0,0.7)',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '30px',
                                    height: '30px',
                                    cursor: 'pointer'
                                }}
                            >✕</button>
                            <img src={selectedImage} alt="Proof" style={{ width: '100%', height: 'auto', display: 'block' }} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManualPaymentsList;
